import { instantOf } from '../freshness.js'
import FantasyPanel from './Fantasy.jsx'
import GameCard from './GameCard.jsx'
import { chipsFor, panelModel } from './fantasyModel.js'
import { FLOOR_WIDE_PX, useMinWidth } from './layout.js'
import { headline, nextLine, orderedCards } from './model.js'
import { dayLabel, tipTime, zoneName } from './time.js'

// Tonight: the headline, the board, and the no-games night. P6-328b (GN-1b).

/**
 * The first tip after a night with no games, when the bot says there is one.
 *
 * 🔴 `next_game` IS THE ONLY PLACE ANYTHING ABOUT ANOTHER DAY COMES FROM. The producer writes it on
 * every board (P6-328a4): `{ id, tip_utc, date_et }` for the earliest scheduled game whose EASTERN date
 * is after the board's, or `null` when the schedule could not be read. `date_et` is the Eastern date
 * the producer worked out from `tip_utc`, so a game that tips after midnight UTC and before midnight
 * Eastern is on the EARLIER day, and the day shown here is `date_et`'s and never the tip's UTC date.
 *
 * It is drawn on a night with no games and on no other (a game night carries one too, and ignores it).
 * `null` says that there are no games and nothing more: no date, no "next". So does an ABSENT key (a bot
 * from before the producer wrote it, or a response cached from before it), because deploy order is not
 * ours to choose. The section is drawn only when BOTH halves are readable — a first tip with no day, or
 * a day with no tip, would be half a claim.
 *
 * ⚠️ THE DESIGN SAYS HOW MANY GAMES ("9 games · first tip 12:00 PM EST"), AND SINCE P6-343 THE BOT DOES.
 * `next_game.count` is the games the schedule lists for that Eastern day. A record with no count (a bot from before
 * the field, a response cached from then) reads "First tip 12:00 PM EST": the line is the first tip alone, and a
 * number is never invented to match the drawing (`model.js::nextLine`).
 */
function NextGames({ nextGame, timeZone }) {
  const tip = instantOf(nextGame?.tip_utc)
  const day = dayLabel(nextGame?.date_et)
  if (tip === null || day === null) return null
  return (
    <section className="d2d-hoops__next" data-testid="hoops-next" aria-label="Next games">
      <span className="d2d-hoops__rowlabel d2d-micro">Next games</span>
      <span className="d2d-hoops__nextday">{day}</span>
      <span className="d2d-hoops__nextline" data-testid="hoops-nextline">
        {nextLine(nextGame.count, `${tipTime(tip, nextGame.date_et, timeZone)} ${zoneName(tip, timeZone)}`)}
      </span>
    </section>
  )
}

/**
 * Last night's finals, on a night with no games (the design's `Last night · Wed 25 Nov · finals`). P6-343.
 *
 * Drawn from `tonight.last_night`, which the producer publishes ONLY on a board with no games and ONLY when its date is the
 * day before the board's, so what the label says is true. The cards are the final cards the night drew (leaders and all) and
 * they open in the game view like any other: the producer publishes each game's own object beside them. Nothing is drawn
 * for a record that has no readable date or no game, so there is never a heading over an empty list. The cards are ordered
 * as every final is, by tip.
 */
function LastNight({ lastNight, teams, nowMs, timeZone, onOpen }) {
  const day = dayLabel(lastNight?.date)
  const games = Array.isArray(lastNight?.games) ? lastNight.games : []
  if (day === null || games.length === 0) return null
  return (
    <section className="d2d-hoops__lastnight" data-testid="hoops-lastnight" aria-label={`Last night, ${day}, finals`}>
      <h3 className="d2d-hoops__sectiontitle d2d-micro">Last night {'·'} {day} {'·'} finals</h3>
      <div className="d2d-hoops__cards" data-testid="hoops-lastnight-cards">
        {orderedCards(games, nowMs).map((entry) => (
          <GameCard
            key={entry.card.id}
            entry={entry}
            teams={teams}
            slateDate={lastNight.date}
            nowMs={nowMs}
            timeZone={timeZone}
            onOpen={onOpen}
          />
        ))}
      </div>
    </section>
  )
}

export default function TonightView({ tonight, teams, nowMs, timeZone, onOpen, fantasy = null }) {
  // P6-330b: the one width question the board asks. A live card draws its five on the floor from 720px (design flag 1),
  // and the cards are told rather than each asking.
  const floorWide = useMinWidth(FLOOR_WIDE_PX)
  const games = Array.isArray(tonight.games) ? tonight.games : []
  const entries = orderedCards(games, nowMs)
  const nextGame = tonight.next_game
  const noGames = games.length === 0
  // P6-333a (GN-6a): the member's own league, when they have one (`fantasy` is `{ blob, boxes }` or `null`, and a member
  // outside a league is passed `null`, which draws nothing fantasy-shaped at all). No panel on a night with no games.
  const panel = fantasy === null || noGames ? null : panelModel(fantasy.blob, fantasy.boxes, games)
  const hasNext = noGames && instantOf(nextGame?.tip_utc) !== null && dayLabel(nextGame?.date_et) !== null

  // The kicker names the night. With no games and nothing known about the next, it names no day:
  // the page then says "no games tonight" and nothing that could be read as a date.
  const night = noGames && !hasNext ? null : dayLabel(tonight.board_date)
  // The slate is the ET date the board is named for; a card's own `tip_et` is the fallback, so a
  // board without a date can still say whether a tip is on the viewer's day.
  const slateFor = (card) =>
    typeof tonight.board_date === 'string' && tonight.board_date !== ''
      ? tonight.board_date
      : typeof card.tip_et === 'string'
        ? card.tip_et.slice(0, 10)
        : null

  return (
    <div className="d2d-hoops__tonight" data-testid="hoops-tonight">
      <div className="d2d-hoops__kicker d2d-micro" data-testid="hoops-kicker">
        {night === null ? 'Tonight' : `Tonight · ${night}`}
      </div>
      <h2 className="d2d-hoops__headline" data-testid="hoops-headline">
        {headline(entries.map((entry) => entry.kind))}
      </h2>
      <div className="d2d-hoops__tznote d2d-micro" data-testid="hoops-tznote">
        Times in {zoneName(nowMs, timeZone)}, your time
      </div>

      {hasNext ? <NextGames nextGame={nextGame} timeZone={timeZone} /> : null}

      <FantasyPanel model={panel} />

      {noGames ? (
        <LastNight lastNight={tonight.last_night} teams={teams} nowMs={nowMs} timeZone={timeZone} onOpen={onOpen} />
      ) : null}

      {noGames ? null : (
        <div className="d2d-hoops__cards" data-testid="hoops-cards">
          {entries.map((entry) => (
            <GameCard
              key={entry.card.id}
              entry={entry}
              teams={teams}
              slateDate={slateFor(entry.card)}
              nowMs={nowMs}
              timeZone={timeZone}
              onOpen={onOpen}
              floorWide={floorWide}
              mine={fantasy === null ? [] : chipsFor(fantasy.blob, fantasy.boxes, games, entry.card.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
